<div class="header" >
  <div class="content">
    <div class="contentInner">
      <div class="pageTitle" appReveal>
        <h1 class="headline">Hardware</h1>
      </div>
    </div>
  </div>
</div>
<div class="content">
  <div class="contentInner">
    <p class="intro">Turn any tablet, phone, or smart TV into a meeting room schedule display and meeting room digital signage with Dash, or go further with a purpose-built professional display.  Whatever your needs, Dash has a solution.</p>

    <h2 class="sectionHeader" id="consumer-displays">Tablets, Phones, and Smart TVs</h2>
    <p>Dash works across a broad range of Android and Apple devices, and Dash Enterprise also works on any screen with a modern web browser, including smart TVs.  If you are not sure if your device is supported, please feel free to <a routerLink="/contact">contact us</a>.</p>

    <div class="compat-table-wrap">
      <table class="compat-table" role="table">
        <thead>
        <tr>
          <th scope="col">Device</th>
          <th scope="col">Minimum OS/browser version</th>
          <th scope="col">Supported plans</th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td class="device-cell">
          <span class="device-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2"/>
              <line x1="12" y1="18" x2="12.01" y2="18" stroke-width="2"/>
            </svg>
          </span>
            <span>
            <strong>Android phones &amp; tablets</strong>
            <em>Samsung, Lenovo, Kindle Fire, and others</em>
          </span>
          </td>
          <td data-label="Minimum OS version"><span>Android 5+ <span class="note">*</span></span></td>
          <td data-label="Supported plans">
          <span class="store-badges">
            <span class="store-badge">Free<span class="sr-only"> (included)</span></span>
            <span class="store-badge">Business<span class="sr-only"> (included)</span></span>
            <span class="store-badge">Enterprise<span class="sr-only"> (included)</span></span>
          </span>
          </td>
        </tr>
        <tr>
          <td class="device-cell">
          <span class="device-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="4" y="2" width="16" height="20" rx="2"/>
              <line x1="12" y1="18" x2="12.01" y2="18" stroke-width="2"/>
            </svg>
          </span>
            <span>
            <strong>iPhones &amp; iPads</strong>
            <em>iPad, iPad Mini, iPad Air, iPhone</em>
          </span>
          </td>
          <td data-label="Minimum OS version">iOS / iPadOS 15+</td>
          <td data-label="Supported plans">
          <span class="store-badges">
            <span class="store-badge">Free<span class="sr-only"> (included)</span></span>
            <span class="store-badge">Business<span class="sr-only"> (included)</span></span>
            <span class="store-badge">Enterprise<span class="sr-only"> (included)</span></span>
          </span>
          </td>
        </tr>
        <tr>
          <td class="device-cell">
          <span class="device-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2"/>
              <path d="M8 21h8M12 17v4"/>
            </svg>
          </span>
            <span>
            <strong>Web browsers &amp; Smart TVs</strong>
            <em>Display devices with a modern browser</em>
          </span>
          </td>
          <td data-label="Minimum browser version"><span>Chrome&nbsp;60+ · Edge&nbsp;79+ ·<wbr> Firefox&nbsp;63+ ·<wbr> Safari&nbsp;11.1+&nbsp;<span class="note">**</span></span></td>
          <td data-label="Supported plans">
          <span class="store-badges">
            <span class="store-badge unsupported"><del>Free</del><span class="sr-only"> (not included)</span></span>
            <span class="store-badge unsupported"><del>Business</del><span class="sr-only"> (not included)</span></span>
            <span class="store-badge">Enterprise<span class="sr-only"> (included)</span></span>
          </span>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <p><span class="note">*</span> If your device doesn't include the Google Play Store or Amazon App Store, please <a routerLink="/contact">contact us</a> for a copy of our official APK installer</p>
    <p><span class="note">**</span> You can test Dash's compatibility with your device or browser using our demo: <a href="https://app.get-dash.com/?demo" target="_blank">https://app.get-dash.com/?demo</a></p>

    <h2 class="sectionHeader" id="room-displays">Professional meeting room displays</h2>
    <p>If you're looking for a purpose-built meeting room display, Dash supports a wide range of professional hardware.  On supported displays, Dash can control LED status lights that change color to show whether a room is free or busy.  LED status light support is included in all plans, including Free.</p>
    <p>Here are our recommended manufacturers.</p>
    <div class="manufacturer-container">
      <div class="manufacturer-content">
        <div>
          <h3>PoE Texas Room Schedulers</h3>
          <p>Our hardware partner PoE Texas offers a range of meeting room displays that come with Dash preinstalled.</p>
          <ul>
            <li>Powered via PoE or the included AC adapter</li>
            <li>Designed for continuous operation, with long-lasting displays and no battery to wear out</li>
            <li>Includes a wall mount kit, with additional mounts available</li>
            <li>Built-in LED status light that changes color to show room availability</li>
            <li>Prices starting from <b>$279.99 USD</b></li>
          </ul>
          <p>For more information and to purchase, visit PoE Texas's website.</p>
        </div>
        <div class="button-container">
          <app-illustration alt="PoE Texas Logo" src="/assets/images/poe-texas-logo.png" webpSrc="/assets/images/poe-texas-logo.webp" [clickable]="false" width="384" height="80" class="manufacturer-logo" [cover]="true"></app-illustration>
          <a class="button" href="https://shop.poetexas.com/collections/dash" target="_blank">Visit PoE Texas</a>
        </div>
      </div>
      <app-illustration alt="Dash Meeting Room Display running on a PoE Texas panel" src="/assets/images/poe-texas-display-2.jpg" webpSrc="/assets/images/poe-texas-display-2.webp" backgroundImageOverlayCss="linear-gradient(90deg, rgba(255, 255, 255, 1) 2%, rgba(255, 255, 255, 0.9) 10%, rgba(255, 255, 255, 0) 66%)" class="photo-illustration" [clickable]="false" backgroundPosition="right" [cover]="true"></app-illustration>
    </div>
    <div class="secondary-manufacturers">
      <div class="manufacturer-container">
        <div class="manufacturer-content">
          <div>
          <h3>ProDVX displays</h3>
            <p>ProDVX offers an extensive range of Android touch displays, with options to suit any business.</p>
            <p>Dash supports the LED status lights on the <b>ProDVX APPC-10</b> directly, and on <b>all ProDVX devices</b> through the ProDVX API.  The API is preinstalled on newer devices, and can be easily installed on older devices.</p>
          </div>
          <div class="button-container">
            <app-illustration alt="ProDVX Logo" src="/assets/images/prodvx-logo.png" webpSrc="/assets/images/prodvx-logo.webp" [clickable]="false" width="384" height="80" class="manufacturer-logo" [cover]="true"></app-illustration>
            <a class="button" href="https://www.prodvx.com/solutions/meeting-room-display" target="_blank">Visit ProDVX</a>
          </div>
        </div>
      </div>
      <div class="manufacturer-container">
        <div class="manufacturer-content">
          <div>
          <h3>Qbic displays</h3>
            <p>Qbic offers a wide range of meeting room display panels, as well as desk panels and larger format devices.</p>
            <p>Dash supports the LED status lights on the <b>TD-1050</b>, <b>D-1050</b>, <b>TD-1060 Slim</b>, and <b>TD-1070</b>.</p>
          </div>
          <div class="button-container">
            <app-illustration alt="Qbic Logo" src="/assets/images/qbic-logo.png" webpSrc="/assets/images/qbic-logo.webp" [clickable]="false" width="384" height="80" class="manufacturer-logo" [cover]="true"></app-illustration>
            <a class="button" href="https://www.qbictechnology.com/products/smart-panel-pc/" target="_blank">Visit Qbic</a>
          </div>
        </div>
      </div>
    </div>
    <h3>Other professional displays</h3>
    <p>Dash also works with other professional displays that support Android apps.  If your device doesn't support Android apps, you may still be able to use Dash via the web browser with a Dash Enterprise subscription (see the <i>Tablets, Phones, and Smart TVs</i> section above).</p>

    <p>Dash supports the LED status lights on the following devices:</p>
    <ul>
      <li>Dovit DO-MB-TOUCH-10-WA-B</li>
      <li>Iadea XDS-1078</li>
      <li>Grandstream GSC3575</li>
      <li>PureLink RoomView-110W</li>
      <li>ProLite TW1025LASC-B1PNR</li>
      <li>ProLite TW1025LASC-B3PNR</li>
      <li>Roomminister RMS-1014-Q</li>
    </ul>
    <p>If your device has LED status lights and isn't on the above list, please <a routerLink="/contact">contact us</a> and we'll be happy to add support.</p>

    <h2 class="sectionHeader" id="led-light-enclosure">Tablet Enclosures</h2>
    <div class="manufacturer-container">
      <div class="manufacturer-content">
        <div>
          <h3>W124 Tablet Enclosures</h3>
          <p>Would you like to use Apple devices for your meeting rooms, but still have all the advantages of a dedicated meeting room display?  The <b>W124 Tablet Enclosures by PoE Texas</b> offer an easy way to wall mount your iPad to a wall or mullion.  Each mount offers:</p>
          <div class="enclosure-stats">
            <img class="illustration-large" alt="Dash running on an iPad in a PoE Texas W124 enclosure" src="/assets/images/ipad-wall-mount.png">
            <ul>
              <li>Powered via USB-C with Power Over Ethernet Accessories</li>
              <li>Solid metal construction</li>
              <li>Concealed power cable</li>
              <li>Screw holes for wall mounting</li>
              <li>LED status light that changes color to show room availability</li>
              <li>Available with an optional mullion mount</li>
              <li>Supports iPad 10, 11-inch iPad Air, and 11-inch iPad Pro</li>
            </ul>
            <img class="illustration-small" alt="Dash running on an iPad in a PoE Texas W124 enclosure" src="/assets/images/ipad-wall-mount.png">
          </div>
          <p style="clear:both">The tablet enclosures are available in 4 varieties: black or silver, with or without a mullion mount.
            For more information and to purchase, visit PoE Texas's website.</p>
        </div>
        <div class="button-container">
          <app-illustration alt="PoE Texas Logo" src="/assets/images/poe-texas-logo.png" webpSrc="/assets/images/poe-texas-logo.webp" [clickable]="false" width="384" height="80" class="manufacturer-logo" [cover]="true"></app-illustration>
          <a class="button" href="https://shop.poetexas.com/collections/dash" target="_blank">Visit PoE Texas</a>
        </div>
      </div>
    </div>
  </div>
</div>
